import React, { useMemo, type JSX } from 'react';

import type { LabelColor } from '@pflegenavi/web-components';
import {
  Label,
  TableCell,
  TableRow,
  Typography,
} from '@pflegenavi/web-components';

import {
  TransactionSourceType,
  TransactionInitiatedBy,
} from '@pflegenavi/shared/api';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { useTranslation } from 'react-i18next';
import { TransactionTypeLogo } from '../general/TransactionTypeLogo';
import { Stack, Tooltip } from '@mui/material';
import type { SearchablePayment } from '../../pages/PaymentsPageView';
import CancelLogo from '../../assets/transaction-icons/cancel.svg';
import RefundBlueLogo from '../../assets/transaction-icons/refund-blue.svg';

interface Props {
  row: SearchablePayment;
}

export function FmPaymentTableRow({ row }: Props): JSX.Element {
  const fDate = useFormatDate();
  const { fCurrency } = useFormatting();
  const { t } = useTranslation();

  const {
    amount,
    date,
    initiationDate,
    sourceType,
    disputed,
    cancelled,
    sourceTypeString,
    paymentStateString,
    ibanLast4,
    sequenceID,
    initiatedBy,
  } = row;

  const { additionalSubIcon } = useMemo(() => {
    if (disputed || cancelled) {
      return {
        additionalSubIcon: CancelLogo,
      };
    }
    if (
      sourceType === TransactionSourceType.Dispute ||
      sourceType === TransactionSourceType.CancelledManualDeposit
    ) {
      return {
        additionalSubIcon: RefundBlueLogo,
      };
    }

    return {
      additionalSubIcon: undefined,
    };
  }, [cancelled, disputed, sourceType]);

  const shouldHideIban = useMemo(() => {
    return (
      initiatedBy === TransactionInitiatedBy.Employee &&
      (sourceType === TransactionSourceType.InitialDeposit ||
        sourceType === TransactionSourceType.DirectDebit ||
        sourceType === TransactionSourceType.ManualDeposit ||
        sourceType === TransactionSourceType.FinalSettlement)
    );
  }, [initiatedBy, sourceType]);

  const amountLabelColor = useMemo(() => {
    if (sourceType === TransactionSourceType.FinalSettlement) {
      if (amount < 0) {
        return 'error';
      } else {
        return 'success';
      }
    }
    if (sourceType === TransactionSourceType.InitialDeposit) {
      if (amount < 0) {
        return 'error';
      } else {
        return 'success';
      }
    }
    if (disputed || cancelled) {
      return 'error';
    }
    if (
      sourceType === TransactionSourceType.Dispute ||
      sourceType === TransactionSourceType.CancelledManualDeposit
    ) {
      return 'info';
    }
    return 'success';
  }, [amount, cancelled, disputed, sourceType]);

  return (
    <TableRow hover>
      <TableCell
        align="left"
        sx={{
          color: 'text.secondary',
          fontSize: 13,
        }}
      >
        {sequenceID ?? '--'}
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitle2">
          {fDate(date, YEAR_MONTH_DAY_SHORT_FORMAT)}
        </Typography>
        {initiationDate && (
          <Tooltip
            arrow
            placement="top"
            title={t('mobile.family-member.initiation-date')}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                cursor: 'help',
                display: 'inline-block',
              }}
            >
              {fDate(initiationDate, YEAR_MONTH_DAY_SHORT_FORMAT)}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="left" sx={{ width: 80 }}>
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <TransactionTypeLogo
            size={40}
            logoType={sourceType}
            additionalSubIcon={additionalSubIcon}
          />

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {sourceTypeString}
            </Typography>
            {paymentStateString && (
              <Typography variant="subtitle2" sx={{ color: 'grey.500' }}>
                ({paymentStateString})
              </Typography>
            )}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell align="left">
        <Typography variant="body2">{row.initiatedByName}</Typography>
      </TableCell>
      <TableCell align="left" sx={{ textTransform: 'capitalize' }}>
        {shouldHideIban ? '--' : ibanLast4 ? `**** ${ibanLast4}` : '--'}
      </TableCell>
      <TableCell align="right">
        <Label variant="ghost" color={amountLabelColor as LabelColor}>
          {fCurrency(amount)}
        </Label>
      </TableCell>
    </TableRow>
  );
}
